/**
 *
 * @module streamSampleTelemetryEvent
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/stream-sample.md
 *
 */


import Check from 'check-types';
import Services from './../../services/entry';

const StreamSampleClientPayload = Services.Internal.Telemetry.StreamSampleClientPayload;
const StreamSamplePayload = Services.Internal.Telemetry.StreamSamplePayload;
const DustUrnReference = Services.Internal.Dust.DustUrnReference;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    greater,
    number,
    nonEmptyString,
    object,
    nonEmptyObject
} = Check.assert;

/**
 *
 * @class StreamSampleTelemetryEvent
 * @implements {TelemetryEvent}
 * @since 3.5.0
 * @note was previously part of SDK.Media
 *
 */
export default class StreamSampleTelemetryEvent {

    /**
     *
     * @access public
     * @param {Object} options
     * @param {String} options.playbackSessionId - a unique id for the playback session, may be UUID
     * @param {Number} options.playhead - the current playhead position in milliseconds
     * @param {Number} options.bitrate - the current playback bitrate in kbps (optional)
     * @param {Object} [options.serverData={}] - the server data returned from the Playlist Service call
     * @note server does not appear in the current UML but we need it passed in the constructor here
     *
     */
    constructor(options) {

        const errorMessage = {
            ctor: 'StreamSampleTelemetryEvent(options)',
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get serverData() {
                return `${this.ctor} options.serverData should be an Object`;
            },
            get playbackSessionId() {
                return `${this.ctor} options.playbackSessionId should be a String with length`;
            },
            get playhead() {
                return `${this.ctor} options.playhead should be a Number`;
            },
            get bitrate() {
                return `${this.ctor} options.bitrate should be a Number`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { playbackSessionId, playhead, bitrate, serverData = {} } = options;

        nonEmptyString(playbackSessionId, errorMessage.playbackSessionId, InvalidArgumentException);

        if (Check.assigned(playhead)) {
            greater(playhead, 0, errorMessage.playhead, InvalidArgumentException);
        }

        if (Check.assigned(bitrate)) {
            number(bitrate, errorMessage.bitrate, InvalidArgumentException);
        }

        object(serverData, errorMessage.serverData, InvalidArgumentException);

        /**
         *
         * @access private
         * @type {Object}
         * @note assigned in StreamSamplePayload as { server }
         *
         */
        this.serverData = serverData;

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.playbackSessionId = playbackSessionId;

        /**
         *
         * @access private
         * @type {Number}
         *
         */
        this.playhead = playhead;

        /**
         *
         * @access private
         * @type {Number}
         *
         */
        this.bitrate = bitrate;

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.event = DustUrnReference.streamSample;

        /**
         *
         * @access public
         * @type {Number}
         * @desc a timestamp in milliseconds
         *
         */
        this.timestamp = Date.now();

    }

    /**
     *
     * @access public
     * @returns {Promise<StreamSamplePayload>}
     * @note serverData can be an empty object
     * @example
     *
     * "serverData": {
     *      "event": "stream-sample",
     *      "media_state": "MEDIA_ON",
     *      "cdn": "PRIMARY_AKAMAI",
     *      "playback_scenario": "HTTP_CLOUD_TABLET",
     *      "media_version": "3.2",
     *      "playlist_request_id": "dc49356e-3afd-4e3b-9fcd-ddd5e51ba5ee",
     *      "stream_id": "112342314",
     *      "media_revision_id": "843e3d3f-389d-46f5-907f-3bf448324f"
     *  },
     *  "client": {
     *      "event": "urn:mlbam:api:stream-sample",
     *      "timestamp": 1544638712502,
     *      "play_head": 0,
     *      "playback_session_id": "b21514d5-394e-4618-967a-327aad0f9873",
     *      "bitrate": 2
     *  }
     *
     */
    getPayload() {

        const { serverData, event, playbackSessionId, playhead, bitrate, timestamp } = this;

        const streamSampleClientPayload = new StreamSampleClientPayload(
            event,
            timestamp,
            playbackSessionId,
            playhead,
            bitrate
        );

        return Promise.resolve(new StreamSamplePayload(serverData, streamSampleClientPayload));

    }

}



// WEBPACK FOOTER //
// ./src/internal/telemetry/streamSampleTelemetryEvent.js